// Tournaments (contracts/tournaments.md): brackets, prizes and the cups.* config keys, with the checks
// backend/api makes (src/domain/cups.ts and services/cups.ts validateInput), mirrored so the editor can say what
// blocks a save before it's sent. Shared by the pages and the mock (src/mocks/db.ts validates PUT /config/cups.*
// with these schemas).

import { z } from "zod";
import { LOCALES } from "../api/messaging";

// ---------------------------------------------------------------- vocabulary

export const CUP_KINDS = ["weekly", "championship", "special"] as const;
export type CupKind = (typeof CUP_KINDS)[number];
/** `scheduled → open → closed` follow the clock; draft, finalized and cancelled are stored. Drafts are hidden from players. */
export const CUP_STATUSES = ["draft", "scheduled", "open", "closed", "finalized", "cancelled"] as const;
export type CupStatus = (typeof CUP_STATUSES)[number];
export const PRIZE_KINDS = ["xp", "badge", "pro_days", "gift_card", "other"] as const;
export type PrizeKind = (typeof PRIZE_KINDS)[number];
/** Prizes of money value: they wait for the admin's confirmation and need a claim (18+, country). */
export const MONEY_KINDS: ReadonlySet<PrizeKind> = new Set(["gift_card", "other"]);
export const GIFT_CARDS = ["amazon", "google_play"] as const;
export type GiftCard = (typeof GIFT_CARDS)[number];
export const GIFT_CARD_LABEL: Record<GiftCard, string> = { amazon: "Amazon", google_play: "Google Play" };
export const AWARD_STATUSES = ["granted", "pending", "claimed", "sent", "done", "forfeited", "void"] as const;
export type AwardStatus = (typeof AWARD_STATUSES)[number];
export const POOLS = ["cup.rookie", "cup.challenger", "cup.master"] as const;
/** The cup badges (src/domain/badges.ts); a badge prize grants one of these. */
export const CUP_BADGES = ["cup_first", "cup_podium", "cup_winner", "champion"] as const;
/** ISO weekdays: 1 = Monday … 7 = Sunday (config cups.weekly.weekday). */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export const WEEKDAY_NAME: Record<number, string> = { 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday" };

export const KIND_LABEL: Record<CupKind, string> = { weekly: "Weekly Cup", championship: "Championship", special: "Special cup" };
export const STATUS_TONE: Record<CupStatus, string> = { draft: "neutral", scheduled: "info", open: "success", closed: "warning", finalized: "neutral", cancelled: "muted" };
export const PRIZE_KIND_LABEL: Record<PrizeKind, string> = { xp: "XP", badge: "Badge", pro_days: "Pro days", gift_card: "Gift card", other: "Other" };
export const AWARD_TONE: Record<AwardStatus, string> = { granted: "muted", pending: "warning", claimed: "info", sent: "violet", done: "success", forfeited: "muted", void: "danger" };

/**
 * Players read "reward(s)", never "prize(s)" (contracts/tournaments.md §6): the hint on every text field the app
 * shows (the rewards line, the reward labels). Admin-internal wording ("prize queue", "money prizes") stays.
 */
export const REWARD_WORDING_HINT = "Shown in the app: say “reward”, never “prize”.";

/** Text limits (contracts/tournaments.md §11, §6; src/domain/cups.ts). */
export const CUP_LIMITS = { title: 60, subtitle: 120, prizeHeadline: 80, rules: 4000, label: 60, value: 40, bracketName: 30 } as const;
export const SET_SIZE = { min: 5, max: 20 } as const;
export const MAX_BRACKETS = 4;
export const MAX_PRIZES = 60;
export const MAX_TEMPLATE_PRIZES = 40;
/** A cup is open for at most this many days. */
export const MAX_WINDOW_DAYS = 31;
/** A live final's lobby opens this long before T0; T0 must leave it inside the window. */
export const LIVE_LOBBY_MS = 15 * 60_000;

// ---------------------------------------------------------------- shapes (as backend/api's src/domain/cups.ts)

const SLUG = /^[a-z][a-z0-9_]{1,23}$/;
const POOL = /^cup\.[a-z0-9_]{1,24}$/;
const COUNTRY = /^[A-Z]{2}$/;

export const Bracket = z.object({
  id: z.string(),
  /** Shown name for a custom bracket (rookie / challenger / master are localized by the server). */
  name: z.string().optional(),
  minLevel: z.number().int(),
  maxLevel: z.number().int().nullable().optional(),
  pool: z.string(),
});
export type Bracket = z.infer<typeof Bracket>;

/** A prize as the API takes and returns it: `value` is always a string ("300", "7", "USD 50", a badge id). */
export const Prize = z.object({
  bracket: z.string(),
  country: z.string().nullable().optional(),
  rankFrom: z.number().int(),
  rankTo: z.number().int(),
  kind: z.enum(PRIZE_KINDS),
  value: z.string(),
  content: z.partialRecord(z.enum(LOCALES), z.object({ label: z.string().optional() })),
  /** gift_card: the cards the winner picks from (at least one). */
  giftCards: z.array(z.enum(GIFT_CARDS)).optional(),
  minEntrants: z.number().int().optional(),
});
export type Prize = z.infer<typeof Prize>;

/** A prize while edited: every optional field filled in. */
export interface EditPrize {
  bracket: string;
  country: string | null;
  rankFrom: number;
  rankTo: number;
  kind: PrizeKind;
  value: string;
  content: Prize["content"];
  giftCards: GiftCard[];
  minEntrants: number;
}

export const editPrize = (p: Prize): EditPrize => ({
  bracket: p.bracket,
  country: p.country ?? null,
  rankFrom: p.rankFrom,
  rankTo: p.rankTo,
  kind: p.kind,
  value: p.value,
  content: p.content,
  giftCards: (p.giftCards ?? []).filter((g): g is GiftCard => (GIFT_CARDS as readonly string[]).includes(g)),
  minEntrants: p.minEntrants ?? 10,
});

/** The body form: gift cards only on a gift-card prize, labels trimmed, empty languages left out. */
export function prizeOut(p: EditPrize): Prize {
  const content: Prize["content"] = {};
  for (const [l, e] of Object.entries(p.content)) {
    const label = e?.label?.trim();
    if (label) content[l as keyof Prize["content"]] = { label };
  }
  return {
    bracket: p.bracket,
    country: p.country,
    rankFrom: p.rankFrom,
    rankTo: p.rankTo,
    kind: p.kind,
    value: p.value.trim(),
    content,
    ...(p.kind === "gift_card" ? { giftCards: p.giftCards } : {}),
    minEntrants: p.minEntrants,
  };
}

/** The value a new prize of a kind starts with. */
export const DEFAULT_VALUE: Record<PrizeKind, string> = { xp: "300", badge: "cup_podium", pro_days: "7", gift_card: "50", other: "" };

export const DEFAULT_BRACKETS: Bracket[] = [
  { id: "rookie", minLevel: 1, maxLevel: 4, pool: "cup.rookie" },
  { id: "challenger", minLevel: 5, maxLevel: 14, pool: "cup.challenger" },
  { id: "master", minLevel: 15, maxLevel: null, pool: "cup.master" },
];

/** The default Weekly Cup prizes (src/domain/cupDefaults.ts): a week of Pro for the top 3 of every bracket. */
export const DEFAULT_PRIZES: Prize[] = DEFAULT_BRACKETS.map((b) => ({
  bracket: b.id,
  rankFrom: 1,
  rankTo: 3,
  kind: "pro_days" as const,
  value: "7",
  minEntrants: 10,
  content: { en: { label: "7 days of IQ Daily Pro" } },
}));

// ---------------------------------------------------------------- checks

const inRange = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max;

/** Problems with a bracket list, as the API answers them: 1–4, unique ids, from level 1, no overlaps (gaps are allowed), only the top open-ended. */
export function bracketProblems(brackets: readonly Bracket[]): string[] {
  const out: string[] = [];
  if (brackets.length < 1 || brackets.length > MAX_BRACKETS) out.push(`A cup has 1 to ${MAX_BRACKETS} brackets.`);
  const seen = new Set<string>();
  brackets.forEach((b, i) => {
    const at = b.id || `Bracket ${i + 1}`;
    if (!SLUG.test(b.id)) out.push(`Bracket ${i + 1}: the id is 2–24 lowercase letters, digits and _ (e.g. rookie).`);
    if (seen.has(b.id)) out.push(`Bracket "${b.id}" appears twice.`);
    seen.add(b.id);
    if (b.name !== undefined && b.name.trim().length > CUP_LIMITS.bracketName) out.push(`${at}: the name is at most ${CUP_LIMITS.bracketName} characters.`);
    if (!inRange(b.minLevel, 1, 1000)) out.push(`${at}: the lowest level is 1 to 1000.`);
    if (b.maxLevel != null && (!inRange(b.maxLevel, 1, 1000) || b.maxLevel < b.minLevel)) out.push(`${at}: the highest level is at least the lowest (and at most 1000).`);
    if (!POOL.test(b.pool)) out.push(`${at}: pick a puzzle pool (cup.…).`);
  });
  const sorted = [...brackets].sort((a, b) => a.minLevel - b.minLevel);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    if (prev.maxLevel == null || prev.maxLevel >= sorted[i]!.minLevel) {
      out.push(`Brackets "${prev.id}" and "${sorted[i]!.id}" overlap: give every bracket but the top one a highest level below the next one's lowest.`);
    }
  }
  if (sorted[0] && sorted[0].minLevel !== 1) out.push("The lowest bracket must start at level 1, so everyone has a bracket.");
  return [...new Set(out)];
}

/** Problems with the prizes, as the API answers them (src/domain/cups.ts PrizeInput, validateInput). */
export function prizeProblems(prizes: readonly EditPrize[], brackets: readonly Bracket[], max = MAX_PRIZES): string[] {
  const out: string[] = [];
  if (prizes.length > max) out.push(`At most ${max} prizes.`);
  const ids = new Set(brackets.map((b) => b.id));
  prizes.forEach((p, i) => {
    const at = `Prize ${i + 1}`;
    const value = p.value.trim();
    const n = Number(value);
    if (!ids.has(p.bracket)) out.push(`${at}: pick one of the cup's brackets.`);
    if (p.country !== null && !COUNTRY.test(p.country)) out.push(`${at}: the country is a 2-letter code (e.g. US), or empty for the whole bracket.`);
    if (!inRange(p.rankFrom, 1, 1000) || !inRange(p.rankTo, 1, 1000) || p.rankTo < p.rankFrom) out.push(`${at}: ranks run from 1 to 1000 (from ≤ to).`);
    else if (p.rankTo - p.rankFrom >= 100) out.push(`${at}: a prize covers at most 100 ranks.`);
    if (!value || value.length > CUP_LIMITS.value) out.push(`${at}: the value is 1–${CUP_LIMITS.value} characters.`);
    else if (p.kind === "xp" && !(n > 0 && n <= 5000)) out.push(`${at}: XP must be 1–5000.`);
    else if (p.kind === "pro_days" && !inRange(n, 1, 365)) out.push(`${at}: Pro days must be a whole number 1–365.`);
    if (p.kind === "gift_card" && p.giftCards.length === 0) out.push(`${at}: pick the gift cards the winner can choose from.`);
    const label = p.content.en?.label?.trim() ?? "";
    if (!label) out.push(`${at}: write the English label.`);
    for (const [l, e] of Object.entries(p.content)) if ((e?.label?.trim().length ?? 0) > CUP_LIMITS.label) out.push(`${at} (${l}): the label is at most ${CUP_LIMITS.label} characters.`);
    if (!inRange(p.minEntrants, 1, 100_000)) out.push(`${at}: the minimum entrants is 1 or more.`);
  });
  return [...new Set(out)];
}

export const isCountry = (c: string) => COUNTRY.test(c);

/** 🇺🇸 from "US" (regional indicator letters); "" when the code isn't two letters. */
export function flag(country: string | null | undefined): string {
  if (!country || !COUNTRY.test(country)) return "";
  return String.fromCodePoint(...[...country].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

/** The prize's value as people read it: "300 XP", "7 days of Pro", "cup_podium", "USD 50". */
export function prizeValueText(p: { kind: PrizeKind; value: string }): string {
  if (p.kind === "xp") return `${p.value} XP`;
  if (p.kind === "pro_days") return `${p.value} ${p.value === "1" ? "day" : "days"} of Pro`;
  return p.value;
}

// ---------------------------------------------------------------- the cup editor's checks

export interface CupForm {
  kind: CupKind;
  opensAt: string | null;
  closesAt: string | null;
  enTitle: string;
  brackets: Bracket[];
  setSize: number;
  prizes: EditPrize[];
  qualifying: { month: string; bestOf: number; perBracket: number } | null;
  final: { mode: "async" | "live"; liveAt?: string } | null;
}

const monthOf = (d: Date) => d.toISOString().slice(0, 7);

/**
 * What blocks saving a cup: the API's checks (src/domain/cups.ts and CupService.validateInput): an English
 * title, a window that closes after it opens and lasts at most 31 days, valid brackets, set size and prizes,
 * and for a Championship its qualifying and final (a live final's T0 at least 15 minutes after the opening).
 */
export function cupProblems(f: CupForm): string[] {
  const out: string[] = [];
  if (!f.enTitle.trim()) out.push("Write the English title.");
  const opens = f.opensAt ? new Date(f.opensAt) : null;
  const closes = f.closesAt ? new Date(f.closesAt) : null;
  if (!opens || !closes) out.push("Set when the cup opens and closes (UTC).");
  else {
    if (closes <= opens) out.push("The cup must close after it opens.");
    else if (closes.getTime() - opens.getTime() > MAX_WINDOW_DAYS * 86_400_000) out.push(`A cup is open for at most ${MAX_WINDOW_DAYS} days.`);
  }
  out.push(...bracketProblems(f.brackets));
  if (!inRange(f.setSize, SET_SIZE.min, SET_SIZE.max)) out.push(`The set is ${SET_SIZE.min} to ${SET_SIZE.max} puzzles.`);
  out.push(...prizeProblems(f.prizes, f.brackets));
  if (f.kind === "championship") {
    const q = f.qualifying;
    if (!q || !/^\d{4}-(0[1-9]|1[0-2])$/.test(q.month)) out.push("Qualifying: pick the month whose Weekly Cups count.");
    if (q && !inRange(q.bestOf, 1, 5)) out.push("Qualifying: the best 1 to 5 Weekly Cups count.");
    if (q && !inRange(q.perBracket, 2, 100)) out.push("Qualifying: 2 to 100 qualify per bracket.");
    if (!f.final) out.push("A Championship needs a final (async or live).");
    if (f.final?.mode === "live") {
      if (!f.final.liveAt) out.push("A live final needs its start time (UTC).");
      else if (opens && closes) {
        const at = Date.parse(f.final.liveAt);
        if (at - LIVE_LOBBY_MS < opens.getTime() || at > closes.getTime()) out.push("The live final starts inside the window, at least 15 minutes after it opens.");
      }
    }
    if (q && opens && monthOf(opens) <= q.month && monthOf(new Date(opens.getTime() - 1)) < q.month) out.push("The final must open after its qualifying month has ended.");
  }
  return [...new Set(out)];
}

/** What the API accepts but is probably a mistake: shown above the save, never blocking it. */
export function cupWarnings(f: CupForm, opts: { isNew: boolean; now?: number }): string[] {
  const out: string[] = [];
  const now = opts.now ?? Date.now();
  if (opts.isNew && f.opensAt && Date.parse(f.opensAt) <= now) out.push("It opens in the past: it starts open (or closed) at once. Useful for testing only.");
  if (opts.isNew && f.kind === "weekly") out.push("Weekly Cups normally come from the template (Settings); this one takes the next Weekly Cup number.");
  if (f.kind === "championship" && f.qualifying && f.opensAt && monthOf(new Date(f.opensAt)) === f.qualifying.month) {
    out.push(`The final opens during its qualifying month (${f.qualifying.month}): the month's last Weekly Cups wouldn't count yet.`);
  }
  return out;
}

// ---------------------------------------------------------------- config (contracts/tournaments.md §9)

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM (24 h)");
const Countries = z.array(z.string().regex(COUNTRY, "2-letter country codes (ISO 3166-1 alpha-2)")).max(250);

const CupText = z.strictObject({
  title: z.string().trim().max(60).optional(),
  subtitle: z.string().trim().max(120).optional(),
  rules: z.string().trim().max(4000).optional(),
  prizeHeadline: z.string().trim().max(80).optional(),
});
export const CupContentBody = z.partialRecord(z.enum(LOCALES), CupText).refine((c) => Boolean(c.en?.title), { message: "content.en needs a title" });

export const BracketBody = z.strictObject({
  id: z.string().regex(SLUG),
  name: z.string().trim().min(1).max(30).optional(),
  minLevel: z.int().min(1).max(1000),
  maxLevel: z.int().min(1).max(1000).nullable().optional(),
  pool: z.string().regex(POOL),
});
export const BracketsBody = z
  .array(BracketBody)
  .min(1)
  .max(MAX_BRACKETS)
  .superRefine((list, ctx) => {
    const problem = bracketProblems(list)[0];
    if (problem) ctx.addIssue({ code: "custom", message: problem });
  });
export const PrizeBody = z
  .strictObject({
    bracket: z.string().min(1).max(24),
    country: z.string().regex(COUNTRY).nullable().optional(),
    rankFrom: z.int().min(1).max(1000),
    rankTo: z.int().min(1).max(1000),
    kind: z.enum(PRIZE_KINDS),
    value: z.string().trim().min(1).max(40),
    content: z.partialRecord(z.enum(LOCALES), z.strictObject({ label: z.string().trim().max(60).optional() })).refine((c) => Boolean(c.en?.label), { message: "content.en needs a label" }),
    giftCards: z.array(z.enum(GIFT_CARDS)).max(2).optional(),
    minEntrants: z.int().min(1).max(100_000).optional(),
  })
  .superRefine((p, ctx) => {
    if (p.rankTo < p.rankFrom) ctx.addIssue({ code: "custom", path: ["rankTo"], message: "rankTo is below rankFrom." });
    if (p.rankTo - p.rankFrom >= 100) ctx.addIssue({ code: "custom", path: ["rankTo"], message: "A prize covers at most 100 ranks." });
    if (p.kind === "xp" && !(Number(p.value) > 0 && Number(p.value) <= 5000)) ctx.addIssue({ code: "custom", path: ["value"], message: "XP must be 1–5000." });
    if (p.kind === "pro_days" && !inRange(Number(p.value), 1, 365)) ctx.addIssue({ code: "custom", path: ["value"], message: "Pro days must be a whole number 1–365." });
    if (p.kind === "gift_card" && !(p.giftCards && p.giftCards.length > 0)) ctx.addIssue({ code: "custom", path: ["giftCards"], message: "Pick the gift cards the winner can choose from." });
  });

/** The Weekly Cup template (config cups.weekly; src/domain/cups.ts WeeklyTemplate). `{number}` in a text is the cup number. */
export const WeeklyTemplate = z.strictObject({
  enabled: z.boolean(),
  /** ISO weekday 1 (Mon) … 7 (Sun) and the UTC time the window opens. */
  weekday: z.int().min(1).max(7),
  time: HHMM,
  durationHours: z.int().min(1).max(168),
  setSize: z.int().min(SET_SIZE.min).max(SET_SIZE.max),
  brackets: BracketsBody,
  prizes: z.array(PrizeBody).max(MAX_TEMPLATE_PRIZES),
  content: CupContentBody,
});
export type WeeklyTemplate = z.infer<typeof WeeklyTemplate>;

export const DEFAULT_WEEKLY: WeeklyTemplate = {
  enabled: true,
  weekday: 5,
  time: "10:00",
  durationHours: 72,
  setSize: 10,
  brackets: DEFAULT_BRACKETS,
  prizes: DEFAULT_PRIZES,
  content: {
    en: {
      title: "Weekly Cup #{number}",
      subtitle: "10 puzzles, one try each. Play any time this weekend.",
      prizeHeadline: "Top 3 in each bracket: 7 days of Pro",
      rules: "Ten puzzles, one try each, in any order, any time while the cup is open. Full Official Rules: iqdaily.app/cups/rules",
    },
  },
};

export const CUPS_CONFIG = {
  "cups.enabled": { schema: z.boolean(), default: false },
  /** Players see no tournaments (features.cups is false) while cups keep running. */
  "cups.hidden": { schema: z.boolean(), default: false },
  "cups.weekly": { schema: WeeklyTemplate, default: DEFAULT_WEEKLY },
  "cups.excludedCountries": { schema: Countries, default: [] as string[] },
  "cups.prizeExcludedCountries": { schema: Countries, default: [] as string[] },
  "cups.claimDays": { schema: z.int().min(1).max(90), default: 14 },
} as const;

export function readCupsConfig(values: Record<string, unknown> | undefined) {
  const get = <K extends keyof typeof CUPS_CONFIG>(k: K) => {
    const parsed = CUPS_CONFIG[k].schema.safeParse(values?.[k]);
    return (parsed.success ? parsed.data : CUPS_CONFIG[k].default) as z.infer<(typeof CUPS_CONFIG)[K]["schema"]>;
  };
  const enabled = get("cups.enabled");
  const hidden = get("cups.hidden");
  return {
    enabled,
    hidden,
    /** What the app reads as features.cups: cups.enabled and not cups.hidden. */
    playersSee: enabled && !hidden,
    weekly: get("cups.weekly"),
    excludedCountries: get("cups.excludedCountries"),
    prizeExcludedCountries: get("cups.prizeExcludedCountries"),
    claimDays: get("cups.claimDays"),
  };
}

// ---------------------------------------------------------------- time

/** Time zones for the local preview of a UTC schedule. */
export const PREVIEW_ZONES = ["America/Los_Angeles", "America/New_York", "Europe/London", "Asia/Dhaka", "Asia/Tokyo"] as const;

export function inZone(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
}

/** Hours between two ISO times (rounded to 0.1). */
export const hoursBetween = (from: string, to: string) => Math.round(((Date.parse(to) - Date.parse(from)) / 3_600_000) * 10) / 10;

/** The next weekly window start at ISO `weekday` + `time` (UTC) strictly after `now` (as src/domain/cups.ts nextWeeklyOpen). */
export function nextOpening(weekday: number, time: string, now = Date.now()): string | null {
  const hm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!hm || !inRange(weekday, 1, 7)) return null;
  const after = new Date(now);
  const d = new Date(Date.UTC(after.getUTCFullYear(), after.getUTCMonth(), after.getUTCDate(), Number(hm[1]), Number(hm[2])));
  const isoDow = ((d.getUTCDay() + 6) % 7) + 1;
  d.setUTCDate(d.getUTCDate() + ((weekday - isoDow + 7) % 7));
  while (d.getTime() <= now) d.setUTCDate(d.getUTCDate() + 7);
  return d.toISOString();
}

// ---------------------------------------------------------------- cup puzzles

/** A pool's bracket as people read it: "cup.rookie" → "Rookie". */
export function poolName(pool: string): string {
  const name = pool.replace(/^cup\./, "").replace(/_/g, " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** A cup set's k, its number in the week: "cup-2026-W41-10042" → "10042". */
export const setNumber = (setId: string) => setId.slice(setId.lastIndexOf("-") + 1);
